import type { AssetId } from '../hooks/useAssetId';
import type { ProjectId } from '../hooks/useProjectId';
import {
    addToBucket,
    removeFromBucket,
    removeFromCanvas,
    setDueDate as setDueDateCommand,
    editDueDate,
} from './AssetCommandDescriptors';
import type { AssetCommand } from './AssetCommand';
import { DefaultCommand } from 'owa-timestream-asset-sdk-types';
import assetCommandsStore from './assetCommandsStore';
import {
    getBucketsContextualMenuItems,
    removeAssetsFromBucket,
} from 'owa-timestream-asset-sdk-bucket';
import { getDueDateMenuPicker, DueDateMenuPickerWrapper } from 'owa-timestream-asset-sdk-duedates';
import { getAsset } from 'owa-timestream-store';
import { getAssetDescriptor } from '../assetsCache';
import changeColorCommand from 'owa-timestream-asset-sdk-color/lib/changeColorCommand';
import { resizeAssetCommand } from 'owa-timestream-asset-sdk-resize';

export default function getAssetCommands(
    projectId: ProjectId,
    selectedAssets: AssetId[],
    onRemoveFromCanvas: () => void
): AssetCommand[] {
    let commands: AssetCommand[] = [];
    let unsupportedCommands: Set<DefaultCommand> = new Set();
    const firstSelectedAssetId = selectedAssets[0];
    const firstSelectedAsset = getAsset(projectId, firstSelectedAssetId);
    const firstSelectedAssetDesc = getAssetDescriptor(firstSelectedAsset.type);

    if (selectedAssets.length === 1) {
        commands = assetCommandsStore.assetCommandsByAssetId.get(firstSelectedAssetId) || [];
        firstSelectedAssetDesc?.unsupportedDefaultCommands.forEach(unsupportedCommand => {
            unsupportedCommands.add(unsupportedCommand);
        });
    } else if (areSelectedAssetsSameType(projectId, selectedAssets)) {
        commands = assetCommandsStore.assetCommandsByType.get(firstSelectedAsset.type) || [];
        firstSelectedAssetDesc?.unsupportedDefaultCommands.forEach(unsupportedCommand => {
            unsupportedCommands.add(unsupportedCommand);
        });
    } else {
        for (let i = 0; i < selectedAssets.length; i++) {
            const { type } = getAsset(projectId, selectedAssets[i]);
            const assetDesc = getAssetDescriptor(type);
            assetDesc?.unsupportedDefaultCommands.forEach(unsupportedCommand => {
                unsupportedCommands.add(unsupportedCommand);
            });
        }
    }

    if (commands.length > 0) {
        commands = commands.filter(command => !command.hidden);
    }

    // Add the supported default commands to change color, add due date, bucket, remove from canvas, and resize
    if (
        selectedAssets.length === 1 &&
        !unsupportedCommands.has(DefaultCommand.Resize) &&
        firstSelectedAssetDesc?.assetResizeConfiguration &&
        !firstSelectedAssetDesc?.assetResizeConfiguration(firstSelectedAsset.payload).resizeDisabled
    ) {
        commands.push(resizeAssetCommand(projectId, firstSelectedAssetId));
    }

    if (selectedAssets.length === 1 && !unsupportedCommands.has(DefaultCommand.DueDate)) {
        // TODO: support multiple selected assets
        const { dueDate, isComplete, payload } = firstSelectedAsset;
        commands.push({
            ...(!!dueDate ? editDueDate : setDueDateCommand),
            disabled: !firstSelectedAssetDesc?.getTitleString(payload),
            getMenuProps: () => ({
                focusZoneAs: DueDateMenuPickerWrapper,
            }),
            getMenuItems: () =>
                getDueDateMenuPicker(
                    projectId,
                    firstSelectedAssetId,
                    dueDate,
                    isComplete,
                    !firstSelectedAssetDesc?.preventFullControlOverTask
                ),
        });
    }

    if (!unsupportedCommands.has(DefaultCommand.Color)) {
        commands.push(changeColorCommand(projectId, selectedAssets, getAssetDescriptor));
    }

    if (!unsupportedCommands.has(DefaultCommand.Bucket)) {
        let canAddToBucket = true;
        let canRemoveFromBucket = true;
        let parentIds: AssetId[] = [];
        for (let i = 0; i < selectedAssets.length; i++) {
            const { type, parentId } = getAsset(projectId, selectedAssets[i]);
            const isBucket = type === 'bucket';
            const isInBucket = !!parentId && getAsset(projectId, parentId)?.type === 'bucket';
            canAddToBucket &&= !isBucket && !isInBucket;
            canRemoveFromBucket &&= isInBucket;
            parentIds.push(parentId as AssetId);
        }
        const bucketItems = canAddToBucket ? getBucketsContextualMenuItems() : [];
        if (bucketItems.length) {
            commands.push({
                ...addToBucket,
                getMenuItems: () => bucketItems,
            });
        }
        if (canRemoveFromBucket) {
            commands.push({
                ...removeFromBucket,
                onInvoke: () => {
                    removeAssetsFromBucket(projectId, selectedAssets, parentIds);
                },
            });
        }
    }

    commands.push({
        ...removeFromCanvas,
        onInvoke: onRemoveFromCanvas,
    });

    return commands;
}

function areSelectedAssetsSameType(projectId: ProjectId, selectedAssets: AssetId[]): boolean {
    if (selectedAssets.length < 1) {
        return false;
    }

    const typeToCompare = getAsset(projectId, selectedAssets[0])?.type;
    if (!typeToCompare) {
        // if the first selected asset couldn't be found return false
        return false;
    }

    return selectedAssets.every(assetId => getAsset(projectId, assetId)?.type === typeToCompare);
}
